import { useEffect, useState, useMemo } from 'react';
import financasApi from '@/lib/financasApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Search, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '-';
const FORMAS = ['Dinheiro', 'PIX', 'Cartão Débito', 'Cartão Crédito', 'Boleto', 'Transferência'];
const STATUS_LABEL = { pago: 'Pago', pendente: 'Pendente', atrasado: 'Atrasado' };
const STATUS_COLOR = {
  pago: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  pendente: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  atrasado: 'bg-red-500/20 text-red-300 border-red-500/30',
};

export default function TransacoesPage({ tipo, titulo, descricao }) {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    tipo: tipo || 'entrada',
    valor: '',
    categoria_id: '',
    descricao: '',
    data: new Date().toISOString().slice(0, 10),
    data_vencimento: '',
    forma_pagamento: 'Dinheiro',
    status: 'pago',
    cliente: '',
    fornecedor: '',
    observacao: '',
  });

  const load = async () => {
    const params = new URLSearchParams();
    if (tipo) params.append('tipo', tipo);
    const { data } = await financasApi.get(`/financas/transacoes?${params.toString()}`);
    setItems(data);
  };

  useEffect(() => {
    load();
    financasApi.get('/financas/categorias').then(({ data }) => setCategorias(data)).catch(() => {});
  }, [tipo]);

  const categoriasFiltradas = useMemo(
    () => categorias.filter(c => c.tipo === tipo || c.tipo === 'ambos'),
    [categorias, tipo]
  );

  const filtered = useMemo(() => {
    let arr = items;
    if (statusFilter !== 'todos') arr = arr.filter(i => i.status === statusFilter);
    if (busca) {
      const b = busca.toLowerCase();
      arr = arr.filter(i =>
        (i.descricao || '').toLowerCase().includes(b) ||
        (i.categoria_nome || '').toLowerCase().includes(b) ||
        (i.cliente || '').toLowerCase().includes(b) ||
        (i.fornecedor || '').toLowerCase().includes(b)
      );
    }
    return arr;
  }, [items, statusFilter, busca]);

  const totais = useMemo(() => {
    const t = { total: 0, pago: 0, pendente: 0 };
    filtered.forEach(i => {
      t.total += i.valor;
      if (i.status === 'pago') t.pago += i.valor;
      else t.pendente += i.valor;
    });
    return t;
  }, [filtered]);

  const openNovo = () => {
    setEditing(null);
    setForm({
      tipo: tipo || 'entrada',
      valor: '',
      categoria_id: '',
      descricao: '',
      data: new Date().toISOString().slice(0, 10),
      data_vencimento: '',
      forma_pagamento: 'Dinheiro',
      status: 'pago',
      cliente: '',
      fornecedor: '',
      observacao: '',
    });
    setDialogOpen(true);
  };

  const openEditar = (item) => {
    setEditing(item);
    setForm({
      tipo: item.tipo,
      valor: String(item.valor),
      categoria_id: item.categoria_id || '',
      descricao: item.descricao || '',
      data: item.data || new Date().toISOString().slice(0, 10),
      data_vencimento: item.data_vencimento || '',
      forma_pagamento: item.forma_pagamento || 'Dinheiro',
      status: item.status || 'pago',
      cliente: item.cliente || '',
      fornecedor: item.fornecedor || '',
      observacao: item.observacao || '',
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.descricao.trim()) { toast.error('Descrição obrigatória'); return; }
    if (!form.valor || Number(form.valor) <= 0) { toast.error('Valor inválido'); return; }
    const cat = categorias.find(c => c.id === form.categoria_id);
    const payload = {
      tipo: form.tipo,
      valor: Number(form.valor),
      categoria_id: form.categoria_id || null,
      categoria_nome: cat ? cat.nome : 'Sem categoria',
      descricao: form.descricao,
      data: form.data,
      data_vencimento: form.data_vencimento || null,
      forma_pagamento: form.forma_pagamento,
      status: form.status,
      cliente: form.cliente || null,
      fornecedor: form.fornecedor || null,
      observacao: form.observacao || null,
    };
    try {
      if (editing) {
        await financasApi.put(`/financas/transacoes/${editing.id}`, payload);
        toast.success('Transação atualizada');
      } else {
        await financasApi.post('/financas/transacoes', payload);
        toast.success('Transação criada');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const excluir = async (item) => {
    if (!window.confirm(`Excluir "${item.descricao}"?`)) return;
    try {
      await financasApi.delete(`/financas/transacoes/${item.id}`);
      toast.success('Excluído');
      load();
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const isEntrada = tipo === 'entrada';
  const cor = isEntrada ? 'emerald' : 'red';
  const Icon = isEntrada ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid={`financas-${tipo}-page`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Icon className={`w-6 h-6 md:w-7 md:h-7 text-${cor}-400`} />
            {titulo}
          </h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1">{descricao}</p>
        </div>
        <Button onClick={openNovo} data-testid={`financas-${tipo}-novo-btn`} className={`bg-${cor}-600 hover:bg-${cor}-500 text-white`}>
          <Plus className="w-4 h-4 mr-2" /> Nova {isEntrada ? 'Entrada' : 'Saída'}
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
        <div className="bg-slate-900 border border-white/10 rounded-xl p-3 md:p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Total</p>
          <p className={`text-lg md:text-2xl font-bold text-${cor}-400 mt-1 truncate`}>{fmtBRL(totais.total)}</p>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-3 md:p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Pago</p>
          <p className="text-lg md:text-2xl font-bold text-emerald-400 mt-1 truncate">{fmtBRL(totais.pago)}</p>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-3 md:p-4">
          <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider">Pendente</p>
          <p className="text-lg md:text-2xl font-bold text-amber-400 mt-1 truncate">{fmtBRL(totais.pendente)}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid={`financas-${tipo}-busca`}
            placeholder="Buscar por descrição, categoria, cliente ou fornecedor..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="bg-slate-900 border-white/10 text-white pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-slate-900 border-white/10 text-white w-full sm:w-44" data-testid={`financas-${tipo}-status-filter`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/10 text-white">
            <SelectItem value="todos">Todos status</SelectItem>
            <SelectItem value="pago">Pago</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="atrasado">Atrasado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cards no mobile */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-slate-900 border border-white/10 rounded-xl py-12 text-center text-slate-500">Nenhuma transação encontrada</div>
        ) : filtered.map(item => (
          <div key={item.id} className="bg-slate-900 border border-white/10 rounded-xl p-3 active:bg-white/5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-medium truncate">{item.descricao}</p>
                <p className="text-xs text-slate-400 mt-0.5">{fmtDate(item.data)} · {item.forma_pagamento}</p>
                {(item.cliente || item.fornecedor) && <p className="text-xs text-slate-500 truncate">{item.cliente || item.fornecedor}</p>}
              </div>
              <p className={`font-semibold text-${cor}-400 whitespace-nowrap text-sm`}>{fmtBRL(item.valor)}</p>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] ${STATUS_COLOR[item.status]}`}>{STATUS_LABEL[item.status]}</span>
                <span className="text-xs text-slate-500 truncate">{item.categoria_nome}</span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEditar(item)} className="text-slate-400 h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => excluir(item)} className="text-red-500 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabela desktop */}
      <div className="hidden md:block bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/50">
            <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Descrição</th>
              <th className="px-4 py-3">Categoria</th>
              <th className="px-4 py-3">Pagamento</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Valor</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Nenhuma transação encontrada</td></tr>
            ) : filtered.map(item => (
              <tr key={item.id} className="hover:bg-white/5">
                <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(item.data)}</td>
                <td className="px-4 py-3 text-white">
                  <div>{item.descricao}</div>
                  {(item.cliente || item.fornecedor) && <div className="text-xs text-slate-500">{item.cliente || item.fornecedor}</div>}
                </td>
                <td className="px-4 py-3 text-slate-400">{item.categoria_nome || '-'}</td>
                <td className="px-4 py-3 text-slate-400">{item.forma_pagamento}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full border text-xs ${STATUS_COLOR[item.status]}`}>{STATUS_LABEL[item.status]}</span>
                </td>
                <td className={`px-4 py-3 text-right font-semibold text-${cor}-400`}>{fmtBRL(item.valor)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEditar(item)} className="text-slate-400 hover:text-white" data-testid={`financas-${tipo}-editar-${item.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => excluir(item)} className="text-red-500 hover:text-red-400" data-testid={`financas-${tipo}-excluir-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar' : 'Nova'} {isEntrada ? 'Entrada' : 'Saída'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Preencha os dados {isEntrada ? 'da receita' : 'da despesa'}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Valor *</Label>
                <Input data-testid={`financas-${tipo}-form-valor`} type="number" step="0.01" min="0" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="bg-slate-950 border-white/10 text-white" required />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Data *</Label>
                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="bg-slate-950 border-white/10 text-white" required />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Descrição *</Label>
              <Input data-testid={`financas-${tipo}-form-descricao`} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="bg-slate-950 border-white/10 text-white" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Categoria</Label>
                <Select value={form.categoria_id} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    {categoriasFiltradas.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Forma de Pagamento</Label>
                <Select value={form.forma_pagamento} onValueChange={(v) => setForm({ ...form, forma_pagamento: v })}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    {FORMAS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="pago">Pago</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="atrasado">Atrasado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Vencimento</Label>
                <Input type="date" value={form.data_vencimento} onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">{isEntrada ? 'Cliente' : 'Fornecedor'}</Label>
              <Input
                value={isEntrada ? form.cliente : form.fornecedor}
                onChange={(e) => setForm(isEntrada ? { ...form, cliente: e.target.value } : { ...form, fornecedor: e.target.value })}
                className="bg-slate-950 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Observação</Label>
              <Textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} className="bg-slate-950 border-white/10 text-white" rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button type="submit" data-testid={`financas-${tipo}-form-salvar`} className={`bg-${cor}-600 hover:bg-${cor}-500 text-white`}>{editing ? 'Atualizar' : 'Criar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
